import {compact, every, filter, find, flatMap, isUndefined, last, orderBy} from 'lodash';

import {CompanyModel} from '../../../models/api/companyModel';
import {MessageStatusesEnum} from '../../../models/api/messageModel';
import {RecipientModel, RecipientRolesEnum} from '../../../models/api/recipientModel';
import {
  ApiRule,
  ApiRules,
  RuleComparatorTypesEnum,
  RuleCompoundConditionTypesEnum,
  RuleModel,
} from '../../../models/api/ruleModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {fetchApiAsync, postApiAsync} from '../../../services/http/http';
import {RuleActionTypesEnum} from '../../../settings/rules/ruleActionModel';
import {RuleEventTypesEnum} from '../../../settings/rules/ruleEventModel';
import {ThunkAction} from '../../appThunk';
import {getCompany} from '../company/companySelectors';
import {makeGetVisualRecipientMap} from '../contacts/recipientsSelectors';
import {
  toggleLastRuleCreatedAction,
  toggleRuleFromDragOptionsAction,
} from '../localPreferences/localPreferencesActions';
import {getStarredTagIds} from '../tags/tagsSelectors';
import {getUser, getUserNamespace} from '../teammates/teammatesSelectors';

/*
 * Action creators.
 */

export interface RuleTemplate {
  tag: TagModel;
  recipients: ReadonlyArray<VisualRecipientModel>;
}

function buildEmailConditions(recipientEmails: ReadonlyArray<string>) {
  return recipientEmails.map((email) => ({
    integration_id: 0,
    type: RecipientRolesEnum.FROM,
    params: {
      comparator: RuleComparatorTypesEnum.EQUALS,
      text: email,
    },
  }));
}

export function createQuickRuleAction(options: RuleTemplate): ThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    const user = getUser(state);
    const company = getCompany(state);
    const {tag, recipients} = options;

    const recipientEmails = recipients.map((recipient) => recipient.contact.handle);
    const ruleName = `Move all messages from ${recipientEmails.join(', ')} to ${tag.name}`;
    const url = `${company.url}/rules`;
    const body = buildCreateQuickRuleBody(ruleName, user, tag, recipientEmails);

    const duplicateRule = await maybeFindDuplicateRuleAsync(ruleName, company, user);
    const rule = duplicateRule || (await postApiAsync(ApiRule, url, body));

    // Dispatch new rule banner
    dispatch(toggleLastRuleCreatedAction(rule));

    // Remove rule created banner after a delay;
    const bannerTimeout = 8 * 1000;
    setTimeout(() => dispatch(toggleLastRuleCreatedAction(rule, true)), bannerTimeout);
  };
}

function buildCreateQuickRuleBody(
  ruleName: string,
  user: TeammateModel,
  tag: TagModel,
  recipientEmails: ReadonlyArray<string>,
) {
  return {
    name: ruleName,
    namespace: user.namespace,
    active: true,
    stop_processing: false,
    sort_order: 0,
    events: [RuleEventTypesEnum.INBOUND],
    compound_conditions: [
      RuleCompoundConditionTypesEnum.AND,
      [RuleCompoundConditionTypesEnum.OR, ...buildEmailConditions(recipientEmails)],
    ],
    actions: [
      {
        integration_id: 0,
        type: RuleActionTypesEnum.TAG,
        params: {
          tag: tag.id,
        },
      },
      {
        integration_id: 0,
        type: RuleActionTypesEnum.UNTAG,
        params: {
          tag: user.inboxTagId,
        },
      },
    ],
  };
}

async function maybeFindDuplicateRuleAsync(
  ruleName: string,
  company: CompanyModel,
  user: TeammateModel,
): Promise<RuleModel | undefined> {
  const rules = await fetchApiAsync(ApiRules, `${company.url}/namespaces/${user.namespace}/rules`);
  return find(rules, {name: ruleName});
}

function compareEmailSenders(
  messageRecipients: ReadonlyArray<RecipientModel>,
  comparisionRecipient: RecipientModel,
) {
  if (!messageRecipients) {
    return false;
  }

  const fromRecipient = find(messageRecipients, {role: RecipientRolesEnum.FROM});
  if (!fromRecipient) {
    return false;
  }

  return fromRecipient.contactUid === comparisionRecipient.contactUid;
}

export function maybeShowQuickRuleDialog(conversationIds: ReadonlyArray<number>, tagId: number): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const user = getUser(state);
    const {messages, preferences, tags} = state.data;
    const starredTagIds = getStarredTagIds(state);

    // If the tag we're moving to is a starred tag, nothing to do.
    if (starredTagIds.has(tagId)) {
      return;
    }

    if (tagId === user.inboxTagId || !messages) {
      return;
    }

    // Don't open the quick rule dialog if the user has already dismissed it for this tag, or if they have dismissed it
    // for 3 separate tags.
    if (preferences.ignoreQuickRuleTagIds.includes(tagId) || preferences.ignoreQuickRuleTagIds.length > 2) {
      return;
    }

    const tag = tags[tagId];
    if (!tag) {
      return;
    }

    const conversationMessages = conversationIds.map((conversationId) =>
      compact(
        filter(
          messages,
          (message) =>
            !isUndefined(message) &&
            conversationId === message.conversationId &&
            message.status === MessageStatusesEnum.IMPORTED,
        ),
      ),
    );

    if (!conversationMessages.length) {
      return;
    }

    const sortedConversationMessages = conversationMessages.map((unsortedMessages) =>
      orderBy(unsortedMessages, (m) => m.sentAt),
    );
    const lastMessageInConversations = flatMap(sortedConversationMessages.map((sorted) => last(sorted)));
    const sampleLastConversation = lastMessageInConversations.length && lastMessageInConversations[0];

    if (!sampleLastConversation) {
      return;
    }

    const firstConversationSender = find(sampleLastConversation.recipients, {role: RecipientRolesEnum.FROM});

    if (!firstConversationSender) {
      return;
    }

    const sharedSender = every(
      lastMessageInConversations,
      (message) => message && compareEmailSenders(message.recipients, firstConversationSender),
    );

    if (!sharedSender) {
      return;
    }

    const getVisualRecipientMap = makeGetVisualRecipientMap(getUserNamespace, () => sampleLastConversation);
    const visualRecipientMap = getVisualRecipientMap(state, {});
    const visualRecipient = visualRecipientMap.from && visualRecipientMap.from[0];

    if (!visualRecipient) {
      return;
    }

    dispatch(
      toggleRuleFromDragOptionsAction({
        recipients: [visualRecipient],
        tag,
      }),
    );
  };
}
